/**
 * ADR-118 — pluggable storage adapter.
 *
 * Default: web `localStorage`. Pluggable so:
 *   - Tests can substitute an in-memory implementation
 *   - Apps with privacy-mode quirks can substitute a fallback
 *   - Future native variants (Electron Beacon's secure store, mobile
 *     WebView-bridged Keychain access) can plug in without forking
 *     the rest of the lib
 *
 * Keys are versioned (`aegix.auth.v1.<name>`) so we can migrate the
 * storage shape later without colliding with legacy app keys that
 * AIM / SMS / ONE already write directly.
 */

const KEY_PREFIX = 'aegix.auth.v1.';

/**
 * Canonical key set the lib reads/writes. Each adopter app may
 * write additional keys; the lib only owns these.
 */
export const StorageKeys = {
  token: `${KEY_PREFIX}token`,
  refreshToken: `${KEY_PREFIX}refresh`,
  user: `${KEY_PREFIX}user`,
  /**
   * Snapshot of permission codenames. Maintained by
   * `libs/auth-permissions`; the lib reads it only for cross-tab
   * sync and boot-time reconciliation.
   */
  permissions: `${KEY_PREFIX}permissions`,
  /**
   * Active organization persisted by
   * `libs/auth-organization`. Read by both this lib (for the
   * cross-tab sync clear-on-logout sweep) and the organization
   * provider on mount. Tabs sync via the same storage-event channel
   * as the auth state.
   */
  activeOrganization: `${KEY_PREFIX}active_org`,
  /**
   * Cross-tab heartbeat. Updating this key from one tab triggers
   * the `storage` event in others.
   */
  heartbeat: `${KEY_PREFIX}heartbeat`,
} as const;

export type StorageKey = (typeof StorageKeys)[keyof typeof StorageKeys];

/**
 * Storage adapter contract. Implementers MUST be synchronous —
 * async storage (IndexedDB, mobile Keychain) needs its own adapter
 * shape because the lib's boot-time reconciliation runs on the
 * synchronous render path.
 */
export interface StorageAdapter {
  get<T>(key: string): T | null;
  set<T>(key: string, value: T): void;
  remove(key: string): void;
  /** Clears only keys this lib owns (prefixed). Does NOT touch other app state. */
  clearAuthKeys(): void;
}

/**
 * The canonical token-storage serialization rule.
 *
 * `token` and `refreshToken` are persisted as **raw strings**; every
 * other auth key is JSON-encoded. The raw form is what the wire
 * consumers (`@tiaki/http-client`'s `Authorization: Bearer` header,
 * the WebSocket ticket-exchange) read directly — JSON-wrapping a lone
 * string buys nothing and creates an unwrap-or-crash footgun on the
 * AIM boot path.
 *
 * Matched against the *logical* `StorageKeys.*` value (before any
 * per-app key remap), so the rule is identical regardless of the
 * physical key name an app bridges to.
 */
export const DEFAULT_RAW_KEYS: ReadonlySet<string> = new Set<string>([
  StorageKeys.token,
  StorageKeys.refreshToken,
]);

/**
 * A legacy JSON-wrapped string value is `"<inner>"` — it starts with
 * a double-quote. A real JWT never does (it starts with the base64url
 * of `{"alg"…}`, i.e. `eyJ`), so this is an unambiguous discriminator
 * for "this raw-key value was written by the legacy JSON path."
 */
function looksJsonQuoted(raw: string): boolean {
  return raw.length >= 2 && raw.charCodeAt(0) === 0x22 /* " */;
}

/**
 * Configuration for {@link createBridgedStorageAdapter}.
 */
export interface BridgedStorageConfig {
  /**
   * Map a logical `StorageKeys.*` value to the physical key actually
   * written to storage. Defaults to identity. Apps that retain legacy
   * key names (AIM `accessToken`, SMS `aegix_sms_token`) pass a mapper.
   */
  keyMap?: (libKey: string) => string;
  /**
   * Resolve which `Storage` backs a given physical key, or `null` when
   * none is available (SSR / privacy mode → the adapter falls back to
   * an in-memory store). Defaults to `window.localStorage` for every
   * key. AIM splits the access token into `sessionStorage`.
   */
  backendFor?: (physicalKey: string) => Storage | null;
  /**
   * Logical keys stored as raw strings rather than JSON. Defaults to
   * {@link DEFAULT_RAW_KEYS} (`token` + `refreshToken`).
   */
  rawKeys?: ReadonlySet<string>;
  /**
   * One-shot legacy-key promotion.
   *
   * For each logical `StorageKeys.*` value, returns the ordered list
   * of legacy physical keys whose values should be promoted to the
   * canonical (`keyMap(libKey)`) location on first read and then
   * deleted. The first non-null legacy value wins; the rest are left
   * untouched on this read but will be cleaned up by `clearAuthKeys`
   * on the next logout sweep (since `clearAuthKeys` already enumerates
   * legacy keys, see below).
   *
   * Canonical always wins over legacy: if the canonical key has a
   * value (even an empty string), legacy keys are not consulted. So
   * the lookup is no-op once the migration has run for a user.
   *
   * The `__cookie__` sentinel from the old cookie-only path is skipped
   * during promotion (the legacy entry is deleted but never copied
   * into canonical) so we don't immediately re-trigger the canonical
   * scrub and thrash storage.
   *
   * Apps adopt the canonical `aegix.auth.v1.*` key namespace by
   * removing their legacy `keyMap` and declaring their old physical
   * names here — existing in-browser sessions migrate forward
   * losslessly (the "no-squash" gate).
   */
  legacyKeys?: (libKey: string) => readonly string[];
}

/**
 * The single token-storage adapter every app builds on.
 *
 * Encapsulates, in one place: logical→physical key mapping, per-key
 * backend selection (session vs local), the raw-token / JSON-rest
 * serialization rule, defensive try/catch, an in-memory fallback when
 * storage is unavailable, and **one-shot self-heal of legacy
 * JSON-quoted token values** left in browsers by the legacy JSON
 * serialization path (unwrap on read, rewrite raw).
 */
export function createBridgedStorageAdapter(
  config: BridgedStorageConfig = {},
): StorageAdapter {
  const keyMap = config.keyMap ?? ((k: string) => k);
  const rawKeys = config.rawKeys ?? DEFAULT_RAW_KEYS;
  const legacyKeysFor = config.legacyKeys;
  const backendFor =
    config.backendFor ??
    ((): Storage | null =>
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined'
        ? window.localStorage
        : null);

  // Fallback store for keys whose backend is unavailable (SSR, privacy
  // mode, sandboxed iframe). Per-instance, so each adapter is isolated.
  const mem = new Map<string, string>();

  const readRaw = (physical: string): string | null => {
    const backend = backendFor(physical);
    if (!backend) return mem.has(physical) ? (mem.get(physical) as string) : null;
    try {
      return backend.getItem(physical);
    } catch (err) {
      console.warn('[auth-core] storage.get failed:', physical, err);
      return null;
    }
  };

  const writeRaw = (physical: string, value: string): void => {
    const backend = backendFor(physical);
    if (!backend) {
      mem.set(physical, value);
      return;
    }
    try {
      backend.setItem(physical, value);
    } catch (err) {
      console.warn('[auth-core] storage.set failed:', physical, err);
    }
  };

  const deleteRaw = (physical: string): void => {
    const backend = backendFor(physical);
    if (!backend) {
      mem.delete(physical);
      return;
    }
    try {
      backend.removeItem(physical);
    } catch (err) {
      console.warn('[auth-core] storage.remove failed:', physical, err);
    }
  };

  /**
   * One-shot promotion of a legacy storage entry into the
   * canonical slot. Returns the raw string that's now sitting at
   * `canonical`, or null if no usable legacy value was found.
   *
   * Walks the legacy list in declared order; the first non-null,
   * non-`__cookie__` value wins and is copied verbatim to canonical
   * before the legacy entry is deleted. The canonical-side read
   * pipeline below then applies the existing `__cookie__` scrub +
   * JSON-quoted unwrap self-heal — so promoting the legacy value
   * **as-is** (rather than pre-cleaning it) keeps the migration code
   * tiny without changing observable semantics.
   *
   * `__cookie__` legacy entries are deleted but not promoted: copying
   * them to canonical would immediately re-trigger the scrub and
   * thrash storage for no benefit.
   */
  const promoteLegacy = (libKey: string, canonical: string): string | null => {
    if (!legacyKeysFor) return null;
    const candidates = legacyKeysFor(libKey);
    if (!candidates || candidates.length === 0) return null;
    for (const legacy of candidates) {
      if (legacy === canonical) continue;
      const legacyRaw = readRaw(legacy);
      if (legacyRaw === null) continue;
      if (legacyRaw === '__cookie__') {
        deleteRaw(legacy);
        continue;
      }
      writeRaw(canonical, legacyRaw);
      deleteRaw(legacy);
      return legacyRaw;
    }
    return null;
  };

  return {
    get<T>(key: string): T | null {
      const physical = keyMap(key);
      let raw = readRaw(physical);
      if (raw === null) {
        // Canonical empty — try to migrate forward from a legacy slot.
        // Returns the now-canonical raw value; the rest of the read
        // pipeline (scrub + unwrap + JSON.parse) runs unchanged.
        raw = promoteLegacy(key, physical);
      }
      if (raw === null) return null;

      if (rawKeys.has(key)) {
        // Scrub the legacy `__cookie__` sentinel. An earlier
        // cookie-only auth path persisted the literal string `__cookie__`
        // as the access token; `@tiaki/http-client` then stamped
        // `Authorization: JWT __cookie__`, which the backend 401s
        // (defeating cookie fallback → logout on reload). Treat it as
        // absent so the consumer sends no header and uses the cookie, and
        // remove it so it stops poisoning subsequent reads.
        if (raw === '__cookie__') {
          deleteRaw(physical);
          return null;
        }
        // Backward compatibility: a legacy JSON-wrapped token. Unwrap
        // and rewrite raw so it's a one-shot self-heal; the next
        // x-refreshed-token rotation writes raw too.
        if (looksJsonQuoted(raw)) {
          try {
            const unwrapped = JSON.parse(raw);
            if (typeof unwrapped === 'string') {
              writeRaw(physical, unwrapped);
              return unwrapped as unknown as T;
            }
          } catch {
            // Not actually JSON — fall through and return the raw value.
          }
        }
        return raw as unknown as T;
      }

      try {
        return JSON.parse(raw) as T;
      } catch {
        // Tolerate a non-JSON value rather than throwing — the lib's
        // reconciliation handles a missing/garbage value gracefully.
        return raw as unknown as T;
      }
    },

    set<T>(key: string, value: T): void {
      const physical = keyMap(key);
      if (rawKeys.has(key)) {
        writeRaw(physical, String(value));
      } else {
        writeRaw(physical, JSON.stringify(value));
      }
    },

    remove(key: string): void {
      deleteRaw(keyMap(key));
    },

    clearAuthKeys(): void {
      for (const libKey of Object.values(StorageKeys) as string[]) {
        deleteRaw(keyMap(libKey));
        // Also evict any still-present legacy entries for
        // this libKey, so logout fully drains the app's pre-migration
        // storage (e.g. a session that read canonical first and never
        // triggered legacy promotion).
        if (legacyKeysFor) {
          for (const legacy of legacyKeysFor(libKey) ?? []) {
            deleteRaw(legacy);
          }
        }
      }
    },
  };
}

/**
 * Production adapter wrapping `window.localStorage` with defensive
 * try/catch. Disabled-storage (privacy mode, iframe sandboxing) falls
 * back to memory-only state rather than throwing. Follows the
 * canonical raw-token / JSON-rest rule via
 * {@link createBridgedStorageAdapter}.
 */
export function createLocalStorageAdapter(): StorageAdapter {
  return createBridgedStorageAdapter();
}

/**
 * In-memory adapter. Used by tests AND as a fallback when
 * `localStorage` is unavailable. State is per-instance — each call
 * to `createMemoryStorageAdapter()` yields a fresh, isolated store.
 * Follows the same raw-token / JSON-rest rule as the live adapter so
 * tests exercise production serialization behavior.
 */
export function createMemoryStorageAdapter(): StorageAdapter {
  return createBridgedStorageAdapter({ backendFor: () => null });
}

/**
 * The token-storage surface `@tiaki/http-client`'s `createClient({ storage })`
 * expects. Kept structural (no import of the http-client package — that
 * would invert the layering) so any app can satisfy it.
 */
export interface HttpClientStorageConfig {
  getAccessToken(): string | null;
  setAccessToken(token: string): void;
  getRefreshToken(): string | null;
  clearAuthSession(): void;
}

/**
 * The single standard wiring between an app's auth
 * `StorageAdapter` and `@tiaki/http-client`.
 *
 * Every web app builds its http client from the SAME adapter instance
 * it passes to `<AuthProvider storage=…>`, via this helper — so the
 * provider and the request path read/write one source of truth, with
 * the raw-token rule + `__cookie__` scrub + legacy self-heal applied by
 * construction. This replaces the per-app hand-rolled storage configs
 * (AIM via its accessors, SMS reading `localStorage` raw, ONE's inline
 * `readToken`) that drifted and produced app-specific auth bugs.
 *
 * `overrides` lets an app keep a behavior its adapter can't express —
 * e.g. AIM's `clearAuthSession` also clears the `jwt-auth` cookie and
 * preserves a few non-auth keys, which is broader than
 * `adapter.clearAuthKeys()`.
 */
export function httpStorageFromAdapter(
  adapter: StorageAdapter,
  overrides?: Partial<HttpClientStorageConfig>,
): HttpClientStorageConfig {
  return {
    getAccessToken: () => adapter.get<string>(StorageKeys.token) ?? null,
    setAccessToken: (token: string) => adapter.set(StorageKeys.token, token),
    getRefreshToken: () =>
      adapter.get<string>(StorageKeys.refreshToken) ?? null,
    clearAuthSession: () => adapter.clearAuthKeys(),
    ...overrides,
  };
}
